import { useEffect, useState } from 'react';

import { isMobile } from '@/consts/device';
import useAuth from '@/hooks/useAuth';
import categoryHandler from './handlers/categoryHandler';
import presetHandler from './handlers/presetHandler';
import projectJsonHandler from './handlers/projectJsonHandler';
import socialShareHandler from './handlers/socialShareHandler';
import taskHandler from './handlers/taskHandler';
import { getDeeplinkSnapshot } from './snapshot';
import { stripDeeplinkUrl } from './stripUrl';
import type { Deeplink } from './types';
import { DeeplinkKind } from './types';

/**
 * Deeplinks whose handler must complete before the app shell renders. Other
 * deeplinks transition through screens (e.g. task uses the loading overlay)
 * and don't block initial paint. Preset links block on mobile because they
 * may resolve into the preset feed — rendering anything sooner flashes an
 * intermediate screen before the redirect.
 */
const isBlockingDeeplink = (kind: DeeplinkKind): boolean =>
  kind === DeeplinkKind.ProjectJson ||
  (kind === DeeplinkKind.Preset && isMobile);

// `switch` keeps TS narrowing inside each branch — each handler receives
// the precise Extract<Deeplink, { kind: … }> type without a cast. The
// `never` fallthrough makes the switch exhaustive: adding a new
// DeeplinkKind without a branch becomes a type error.
const dispatch = (deeplink: Deeplink): void | Promise<void> => {
  switch (deeplink.kind) {
    case DeeplinkKind.Task:
      return taskHandler(deeplink);
    case DeeplinkKind.ProjectJson:
      return projectJsonHandler(deeplink);
    case DeeplinkKind.Preset:
      return presetHandler(deeplink);
    case DeeplinkKind.Category:
      return categoryHandler(deeplink);
    case DeeplinkKind.SocialShare:
      return socialShareHandler(deeplink);
    case DeeplinkKind.None:
      return;
    default: {
      const _exhaustive: never = deeplink;
      return _exhaustive;
    }
  }
};

// Module-level latch so React Strict Mode (double-mount) doesn't run handlers
// twice. Deeplinks are one-shot inputs — at most one router instance per app
// session may dispatch them.
let hasDispatched = false;

/**
 * Mount once at the app shell. Dispatches the snapshot deeplink to its
 * handler, then strips the URL param. Returns isPending=true while a
 * blocking handler is in flight so the shell can render a loader.
 */
export const useDeeplinkRouter = () => {
  const { isResolved } = useAuth();
  const [isPending, setIsPending] = useState<boolean>(() =>
    isBlockingDeeplink(getDeeplinkSnapshot().kind)
  );

  useEffect(() => {
    if (hasDispatched) {
      setIsPending(false);
      return;
    }

    // Wait for auth to settle before dispatching so handlers decide against a
    // correct auth state (the host populates the user shortly after boot).
    if (!isResolved) return;
    hasDispatched = true;

    const deeplink = getDeeplinkSnapshot();
    if (deeplink.kind === DeeplinkKind.None) {
      setIsPending(false);
      return;
    }

    Promise.resolve(dispatch(deeplink)).finally(() => {
      stripDeeplinkUrl(deeplink.kind);
      setIsPending(false);
    });
  }, [isResolved]);

  return { isPending };
};
